import React, { useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '../../lib/utils';
import { ToolRegistry } from '../../core/tools/ToolRegistry';
import { IToolContext } from '../../core/tools/interfaces/ITool';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  MoreVertical,
  GripVertical,
  Save,
  Download,
  Upload,
  SwatchBook,
  ClipboardPaste,
  MessageSquarePlus,
  Link,
  Lock,
  Unlock,
  BringToFront,
  MoveUp,
  MoveDown,
  SendToBack,
  Copy,
  Trash2,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from '../ui/dropdown-menu';
import './ShapeToolbar.css';
import { useDiagramStore } from '../../stores/diagramStore';
import { getCommandManager } from '../../hooks/useCommandManager';
import { ChangeNodeStyleCommand } from '../../core/commands/ChangeNodeStyleCommand';
import { CopyNodeCommand } from '../../core/commands/CopyNodeCommand';
import { toast } from 'sonner';
import { Logger } from '../../utils/logger';
import { getAllTableSchemas, getTableSchema } from '../../domains/diagram/shapes/table/tableSchemas.registry';
import { ConvertTableSchemaCommand } from '../../core/commands/ConvertTableSchemaCommand';
import { SaveTableTemplateDialog } from '../tables/SaveTableTemplateDialog';
import {
  exportSchemaAsJSON,
  extractSchemaFromTableNode,
  getCustomSchema,
  importSchemaFromJSON,
  saveCustomSchema,
} from '../../domains/diagram/shapes/table/customTableSchemas';
import { useShapeToolbarPlacement } from './useShapeToolbarPlacement';

const logger = Logger.getLoggerWithPrefix('TableShapeToolbar');

interface TableShapeToolbarProps {
  selectedShapes: any[];
  selectionBounds: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  toolRegistry: ToolRegistry;
  toolContext: IToolContext;
  onToolExecute?: (toolId: string, params?: any) => void | Promise<void>;
}

let lastDragOffset = { x: 0, y: 0 };

export const TableShapeToolbar: React.FC<TableShapeToolbarProps> = ({
  selectedShapes,
  selectionBounds,
  toolRegistry,
  toolContext,
  onToolExecute
}) => {
  const { t } = useTranslation('diagram');
  const { t: tc } = useTranslation('common');
  const stage = useDiagramStore((state) => state.stage);
  const nodes = useDiagramStore((state) => state.nodes);
  const [dragOffset, setDragOffset] = React.useState(() => lastDragOffset);
  const [isDragging, setIsDragging] = React.useState(false);
  const dragStartPos = React.useRef({ x: 0, y: 0 });
  const handleForgetDragOffset = React.useCallback(() => {
    const resetOffset = { x: 0, y: 0 };
    lastDragOffset = resetOffset;
    setDragOffset(resetOffset);
  }, []);
  const { toolbarRef, placement } = useShapeToolbarPlacement({
    selectionBounds,
    stage,
    dragOffset,
    isDragging,
    onForgetDragOffset: handleForgetDragOffset,
  });
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = React.useState(false);
  const [schemaToExport, setSchemaToExport] = React.useState<any | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const toolOrder = [
    'shape-color',
    'shape-outline',
    'table-grid',
    'table-cell-style'
  ];

  const orderedTools = useMemo(() => {
    if (selectedShapes.length === 0) return [] as any[];
    const list = toolRegistry.getToolsForLocation('floatingPanel', toolContext.selectionSummary);
    const byId = new Map(list.map(t => [t.manifest.id, t]));
    const tools: any[] = [];
    for (const id of toolOrder) {
      const tool = byId.get(id);
      if (tool && tool.canExecute(toolContext)) tools.push(tool);
    }
    return tools;
  }, [selectedShapes, toolRegistry, toolContext]);

  const groupedTools = useMemo(() => {
    const byId = new Map(orderedTools.map(t => [t.manifest.id, t]));
    // Group 1: Shape color, Shape outline
    const group1 = ['shape-color', 'shape-outline']
      .map(id => byId.get(id))
      .filter(Boolean) as any[];
    // Group 2: Table grid operations (Headers, Merge, Insert, Remove)
    const group2 = ['table-grid']
      .map(id => byId.get(id))
      .filter(Boolean) as any[];
    // Group 3: Table cell style (Cell resize, Cell layout, Text) - separated internally
    const group3 = ['table-cell-style']
      .map(id => byId.get(id))
      .filter(Boolean) as any[];
    return [group1, group2, group3].filter(g => g.length > 0);
  }, [orderedTools]);

  const allLocked = useMemo(() => {
    if (!selectedShapes || selectedShapes.length === 0) return false;
    return selectedShapes.every((s: any) => !!(s?.locked || s?.attrs?.locked));
  }, [selectedShapes]);

  React.useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartPos.current.x;
      const deltaY = e.clientY - dragStartPos.current.y;
      setDragOffset(prev => {
        const next = { x: prev.x + deltaX, y: prev.y + deltaY };
        lastDragOffset = next;
        return next;
      });
      dragStartPos.current = { x: e.clientX, y: e.clientY };
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const handleToolExecute = async (tool: any, params?: any) => {
    try {
      if (onToolExecute) {
        const maybePromise = onToolExecute(tool.manifest.id, params);
        if (maybePromise instanceof Promise) await maybePromise;
      } else {
        await toolRegistry.executeTool(tool.manifest.id, params);
      }
    } catch (error) {
      logger.error(`Failed to execute tool ${tool.manifest.id}:`, error);
    }
  };

  const handleLockToggle = async () => {
    try {
      const selectedIds = Array.from(toolContext.selection || new Set<string>());
      if (selectedIds.length === 0) return;
      const cmd = new ChangeNodeStyleCommand(selectedIds, 'locked', !allLocked);
      const commandManager = getCommandManager();
      await commandManager.execute(cmd);
    } catch (err) {
      logger.error('Failed to lock selection', err);
    }
  };

  // In-memory style clipboard (page scoped) – share across toolbar uses
  const styleClipboard: { styles?: Record<string, any> } = (window as any).__STYLE_CLIPBOARD__ || ((window as any).__STYLE_CLIPBOARD__ = {});

  // Collect normalized styles from a shape (fill, outline, text)
  const collectStyles = (shape: any): Record<string, any> => {
    const attrs = (shape?.attrs || {}) as Record<string, any>;
    return {
      fillColor: shape.fillColor ?? attrs.fillColor,
      strokeColor: shape.strokeColor ?? attrs.strokeColor,
      strokeWidth: shape.strokeWidth ?? attrs.strokeWidth ?? attrs.borderWidth,
      dash: Array.isArray(attrs.dash)
        ? attrs.dash
        : (Array.isArray((attrs as any).borderDash) ? (attrs as any).borderDash : undefined),
      dashEnabled: typeof attrs.dashEnabled === 'boolean' ? attrs.dashEnabled : undefined,
      fontFamily: attrs.fontFamily ?? shape.fontFamily,
      fontSize: attrs.fontSize ?? shape.fontSize,
      fontWeight: attrs.fontWeight,
      fontStyle: attrs.fontStyle,
      textDecoration: attrs.textDecoration,
      textAlign: attrs.textAlign,
      verticalAlign: attrs.verticalAlign,
      textColor: attrs.textColor,
    };
  };

  const handleCopyStyle = async () => {
    const first = selectedShapes[0];
    if (!first) return;
    const styles = collectStyles(first);
    styleClipboard.styles = styles;
    try {
      await navigator.clipboard?.writeText?.(JSON.stringify({ type: 'creately-style', styles }));
    } catch {}
  };

  const handlePasteStyle = async () => {
    const cm = getCommandManager();
    let styles = styleClipboard.styles;
    if (!styles) {
      try {
        const text = await navigator.clipboard?.readText?.();
        const parsed = JSON.parse(text || '{}');
        if (parsed && parsed.type === 'creately-style') styles = parsed.styles as Record<string, any>;
      } catch {}
    }
    if (!styles) return;
    const ids = selectedShapes.map((s) => s.id);
    const apply = async (key: string, value: any) => {
      if (value === undefined) return;
      try {
        await cm.execute(new ChangeNodeStyleCommand(ids, key, value));
      } catch (err) {
        logger.warn('Paste style failed', key, err);
      }
    };
    await apply('fillColor', styles.fillColor);
    await apply('strokeColor', styles.strokeColor);
    await apply('strokeWidth', styles.strokeWidth);
    await apply('dash', styles.dash);
    await apply('dashEnabled', styles.dashEnabled);
    await apply('fontFamily', styles.fontFamily);
    await apply('fontSize', styles.fontSize);
    await apply('fontWeight', styles.fontWeight);
    await apply('fontStyle', styles.fontStyle);
    await apply('textDecoration', styles.textDecoration);
    await apply('textAlign', styles.textAlign);
    await apply('verticalAlign', styles.verticalAlign);
    await apply('textColor', styles.textColor);
  };

  const handleAddComment = async () => {
    const commentsTool = toolRegistry.getTool('comments');
    if (!commentsTool) {
      logger.warn('Comments tool is not registered; cannot add comment from table toolbar.');
      return;
    }
    await handleToolExecute(commentsTool);
  };

  const handleCopyLink = async () => {
    const first = selectedShapes[0];
    if (!first) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('shape', first.id);
      await navigator.clipboard.writeText(url.toString());
    } catch {}
  };

  const handleZ = async (mode: 'front' | 'forward' | 'backward' | 'back') => {
    try {
      const { AdjustZIndexCommand } = await import('../../core/commands/AdjustZIndexCommand');
      const ids = selectedShapes.map((s) => s.id);
      const cmd = new (AdjustZIndexCommand as any)(ids, mode);
      const cm = getCommandManager();
      await cm.execute(cmd);
    } catch (err) {
      logger.error('Z-index change failed', err);
    }
  };

  const handleCopySelection = async () => {
    const selectedIds = Array.from(toolContext.selection || new Set<string>());
    if (selectedIds.length === 0) return;

    const commandManager = getCommandManager();
    try {
      await commandManager.execute(new CopyNodeCommand(selectedIds));
    } catch (err) {
      logger.error('Copy command failed', err);
    }
  };

  const handleConvertSchema = async (schemaId: string) => {
    try {
      if (!selectedShapes || selectedShapes.length === 0) return;
      const tableId = selectedShapes[0]?.id as string | undefined;
      if (!tableId) return;
      const cmd = new ConvertTableSchemaCommand(tableId, schemaId);
      const commandManager = getCommandManager();
      await commandManager.execute(cmd);
    } catch (err) {
      logger.error('Failed to convert table schema', err);
    }
  };

  const getSelectedTableNode = () => {
    const tableId = selectedShapes?.[0]?.id as string | undefined;
    if (!tableId) return null;
    const node = nodes.find((n: any) => n.id === tableId);
    if (!node || node.type !== 'table') return null;
    return node;
  };

  const downloadJSON = (filename: string, json: string) => {
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportCurrentTemplate = () => {
    const node = getSelectedTableNode();
    if (!node) return;
    const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
    const schemaId = typeof attrs.schemaId === 'string' ? attrs.schemaId : null;
    const resolved = schemaId ? getTableSchema(schemaId) : null;
    const name = resolved?.name || 'Table Template';

    const customSchema = schemaId ? getCustomSchema(schemaId) : null;
    if (customSchema) {
      downloadJSON(`${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`, exportSchemaAsJSON(customSchema));
      return;
    }

    if (resolved) {
      downloadJSON(`${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`, JSON.stringify(resolved, null, 2));
      return;
    }

    const extracted = extractSchemaFromTableNode(
      node as any,
      `export-${Date.now()}`,
      name,
      'custom',
    );
    if (extracted) {
      downloadJSON(`${name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.json`, JSON.stringify(extracted, null, 2));
    }
  };

  const handleOpenImportFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleImportFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = event.target?.result as string;
        const imported = importSchemaFromJSON(json);
        if (imported) {
          saveCustomSchema(imported);
          window.dispatchEvent(new CustomEvent('table-template-imported', { detail: { schemaId: imported.id, name: imported.name } }));
        } else {
          toast.error(tc('tableTemplates.importFailed'));
        }
      } catch (error) {
        logger.error('Failed to import template', error);
        toast.error('Failed to import template. Please check the file format.');
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleOpenSaveDialog = () => {
    const node = getSelectedTableNode();
    if (!node) return;
    const attrs = ((node as any).attrs ?? {}) as Record<string, unknown>;
    const schemaId = typeof attrs.schemaId === 'string' ? attrs.schemaId : null;
    setSchemaToExport(schemaId ? getCustomSchema(schemaId) : null);
    setSaveTemplateDialogOpen(true);
  };

  const handleSaveTemplate = (name: string, category: any) => {
    const node = getSelectedTableNode();
    if (!node) return;
    const schemaId = `custom-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const extracted = extractSchemaFromTableNode(node as any, schemaId, name, category);
    if (!extracted) return;
    const saved = saveCustomSchema(extracted);
    setSchemaToExport(saved);
    window.dispatchEvent(new CustomEvent('table-template-saved', { detail: { schemaId, name } }));
  };

  if (selectedShapes.length === 0 || orderedTools.length === 0) {
    return null;
  }

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartPos.current = { x: e.clientX, y: e.clientY };
  };

  return (
    <div
      ref={toolbarRef}
      data-testid="tid.editor.tableShapeToolbar"
      className={cn(
        "shape-toolbar",
        "absolute z-50",
        "bg-background rounded-[10px]",
        "border border-border",
        "shadow-[0_6px_18px_rgba(2,6,23,0.08)]",
        "grid grid-flow-col auto-cols-max",
        "min-w-fit"
      )}
      style={{
        left: placement.centerX,
        top: placement.topY,
        transform: 'translateX(-50%)',
      }}
    >
      <div
        className={cn(
          "flex items-center justify-center py-1.5 pl-1.5 pr-1 bg-muted/40 rounded-l-[10px]",
          isDragging ? "cursor-grabbing" : "cursor-grab"
        )}
        onMouseDown={handleDragStart}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>

      <Separator orientation="vertical" className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] dark:border-[#3f3f46] mr-2" />

      {groupedTools.map((group, gi) => (
        <React.Fragment key={`group-${gi}`}>
          {gi > 0 && (
            <Separator
              orientation="vertical"
              className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] dark:border-[#3f3f46] mx-2"
            />
          )}
          {group.map((tool) => {
            const ToolComponent = tool.getFloatingPanelComponent?.() || tool.getToolbarComponent?.();
            if (!ToolComponent) return null;
            return (
              <div key={tool.manifest.id} className="flex-shrink-0 flex items-center py-1.5 px-0" data-testid={`tid.editor.tool.${tool.manifest.id}`}>
                <ToolComponent
                  tool={tool}
                  context={toolContext}
                  onExecute={(params: any) => handleToolExecute(tool, params)}
                />
              </div>
            );
          })}
        </React.Fragment>
      ))}

      <Separator orientation="vertical" className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] dark:border-[#3f3f46] ml-2 mr-1" />

      <TooltipProvider>
        <DropdownMenu>
          <Tooltip>
            <DropdownMenuTrigger asChild>
              <TooltipTrigger asChild>
                <div className="flex-shrink-0 flex items-center py-1.5 pr-2 pl-1">
                  <button className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors" data-testid="tid.editor.tableShapeToolbar.more">
                    <MoreVertical className="h-5 w-5 text-foreground" />
                  </button>
                </div>
              </TooltipTrigger>
            </DropdownMenuTrigger>
            <TooltipContent>{t('shapeMenu.more')}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent
            align="end"
            className="w-[200px] max-h-[65vh] overflow-y-auto overscroll-contain"
          >
            <div style={{ display: 'none' }}>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    {t('shapeMenu.convertTableMode', { defaultValue: 'Convert to template' })}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="max-h-[260px] overflow-y-auto overscroll-contain w-[220px]">
                    {getAllTableSchemas()
                      .filter((schema) => schema.id !== 'blank')
                      .map((schema) => (
                        <DropdownMenuItem
                          key={schema.id}
                          onSelect={(e) => {
                            e.preventDefault();
                            void handleConvertSchema(schema.id);
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="text-xs font-medium">{schema.name}</span>
                            <span className="text-[10px] text-muted-foreground">{schema.category}</span>
                          </div>
                        </DropdownMenuItem>
                      ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleOpenSaveDialog();
                  }}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {tc('tableTemplates.saveAsTemplate')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleExportCurrentTemplate();
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  {tc('tableTemplates.exportTemplate')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleOpenImportFilePicker();
                  }}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {tc('tableTemplates.importTemplate')}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

            </div>
            <DropdownMenuItem data-testid="tid.editor.tableMenu.copyStyle" onSelect={handleCopyStyle}>
              <SwatchBook className="mr-1" />
              {t('shapeMenu.copyStyle')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="tid.editor.tableMenu.pasteStyle" onSelect={handlePasteStyle}>
              <ClipboardPaste className="mr-1" />
              {t('shapeMenu.pasteStyle')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="tid.editor.tableMenu.addComment" onSelect={() => { void handleAddComment(); }}>
              <MessageSquarePlus className="mr-1" />
              {t('shapeMenu.addComment')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="tid.editor.tableMenu.copyLink" onSelect={handleCopyLink}>
              <Link className="mr-1" />
              {t('shapeMenu.copyLink')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="tid.editor.tableMenu.lockToggle" onSelect={(e) => { e.preventDefault(); void handleLockToggle(); }}>
              {allLocked ? (
                <>
                  <Unlock className="mr-1" />
                  {t('shapeMenu.unlock')}
                </>
              ) : (
                <>
                  <Lock className="mr-1" />
                  {t('shapeMenu.lock')}
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="tid.editor.tableMenu.bringToFront" onSelect={() => handleZ('front')}>
              <BringToFront className="mr-1" />
              {t('shapeMenu.bringToFront')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="tid.editor.tableMenu.bringForward" onSelect={() => handleZ('forward')}>
              <MoveUp className="mr-1" />
              {t('shapeMenu.bringForward')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="tid.editor.tableMenu.sendBackward" onSelect={() => handleZ('backward')}>
              <MoveDown className="mr-1" />
              {t('shapeMenu.sendBackward')}
            </DropdownMenuItem>
            <DropdownMenuItem data-testid="tid.editor.tableMenu.sendToBack" onSelect={() => handleZ('back')}>
              <SendToBack className="mr-1" />
              {t('shapeMenu.sendToBack')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="tid.editor.tableMenu.copy" onSelect={() => { void handleCopySelection(); }}>
              <Copy className="mr-1" />
              {t('shapeMenu.copy')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="tid.editor.tableMenu.delete" className="text-red-600" onSelect={() => { void toolRegistry.executeTool('delete'); }}>
              <Trash2 className="mr-1" />
              {t('shapeMenu.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TooltipProvider>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleImportFileChange}
      />
      <SaveTableTemplateDialog
        open={saveTemplateDialogOpen}
        onOpenChange={(open) => {
          setSaveTemplateDialogOpen(open);
          if (!open) setSchemaToExport(null);
        }}
        onSave={handleSaveTemplate}
        schemaToExport={schemaToExport}
        showExport={!!schemaToExport}
        showImport={true}
      />
    </div>
  );
};
